import { useEffect, useState } from 'react'
import apiClient from '../lib/api-client'

type PageMeta = { total: number; limit: number; offset: number }

export function useOverview(params?: { start?: string; end?: string; tz?: string }) {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await apiClient.get('/v1/analytics/overview', { params })
        setData(res.data)
      } catch (e: any) {
        setError(e?.response?.status === 401 ? 'Unauthorized' : (e?.message ?? 'Failed to load'))
      } finally {
        setLoading(false)
      }
    }
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.start, params?.end, params?.tz])

  return { data, loading, error }
}

export function useTimeseries(metric: string, params?: { start?: string; end?: string }) {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await apiClient.get('/v1/analytics/timeseries', { params: { metric, ...params } })
        setData(res.data)
      } catch (e: any) {
        setError(e?.response?.status === 401 ? 'Unauthorized' : (e?.message ?? 'Failed to load'))
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [metric, params?.start, params?.end])
  return { data, loading, error }
}

export function useAgents(params?: { start?: string; end?: string; limit?: number; offset?: number }) {
  const [items, setItems] = useState<any[]>([])
  const [page, setPage] = useState<PageMeta>({ total: 0, limit: params?.limit ?? 20, offset: params?.offset ?? 0 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await apiClient.get('/v1/analytics/agents', { params })
        setItems(res.data.items)
        setPage(res.data.page)
      } catch (e: any) {
        setError(e?.response?.status === 401 ? 'Unauthorized' : (e?.message ?? 'Failed to load'))
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [params?.start, params?.end, params?.limit, params?.offset])
  return { items, page, loading, error }
}

export function useUsers(params?: { start?: string; end?: string; limit?: number; offset?: number; sort_by?: string }) {
  const [items, setItems] = useState<any[]>([])
  const [page, setPage] = useState<PageMeta>({ total: 0, limit: params?.limit ?? 20, offset: params?.offset ?? 0 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await apiClient.get('/v1/analytics/users', { params })
        setItems(res.data.items)
        setPage(res.data.page)
      } catch (e: any) {
        setError(e?.response?.status === 401 ? 'Unauthorized' : (e?.message ?? 'Failed to load'))
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [params?.start, params?.end, params?.limit, params?.offset, params?.sort_by])
  return { items, page, loading, error }
}

export function useTransactions(params?: { start?: string; end?: string; limit?: number; offset?: number }) {
  const [items, setItems] = useState<any[]>([])
  const [page, setPage] = useState<PageMeta>({ total: 0, limit: params?.limit ?? 20, offset: params?.offset ?? 0 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await apiClient.get('/v1/analytics/transactions', { params })
        setItems(res.data.items)
        setPage(res.data.page)
      } catch (e: any) {
        setError(e?.response?.status === 401 ? 'Unauthorized' : (e?.message ?? 'Failed to load'))
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [params?.start, params?.end, params?.limit, params?.offset])
  return { items, page, loading, error }
}

export function useMessages(params?: { start?: string; end?: string; limit?: number; offset?: number }) {
  const [items, setItems] = useState<any[]>([])
  const [page, setPage] = useState<PageMeta>({ total: 0, limit: params?.limit ?? 20, offset: params?.offset ?? 0 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await apiClient.get('/v1/analytics/messages', { params })
        setItems(res.data.items)
        setPage(res.data.page)
      } catch (e: any) {
        setError(e?.response?.status === 401 ? 'Unauthorized' : (e?.message ?? 'Failed to load'))
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [params?.start, params?.end, params?.limit, params?.offset])
  return { items, page, loading, error }
}

export function useConversations(params?: { start?: string; end?: string; limit?: number; offset?: number }) {
  const [items, setItems] = useState<any[]>([])
  const [page, setPage] = useState<PageMeta>({ total: 0, limit: params?.limit ?? 20, offset: params?.offset ?? 0 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await apiClient.get('/v1/analytics/conversations', { params })
        setItems(res.data.items)
        setPage(res.data.page)
      } catch (e: any) {
        setError(e?.response?.status === 401 ? 'Unauthorized' : (e?.message ?? 'Failed to load'))
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [params?.start, params?.end, params?.limit, params?.offset])
  return { items, page, loading, error }
}

export function useRateCard() {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await apiClient.get('/v1/analytics/rate-card')
        setData(res.data)
      } catch (e: any) {
        setError(e?.response?.status === 401 ? 'Unauthorized' : (e?.message ?? 'Failed to load'))
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [])
  return { data, loading, error }
}


